import React from 'react';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";
import { PRO_FEATURES } from "./upgradeFeatures.ts";
import { openSubscribe } from "../../../Utils/misc/openLink.ts";
import { submitFeedback } from "../Settings/SettingsContent/Feedback/submitFeedback.ts";

interface UpgradeFeatureBreakdownProps {}

const UpgradeFeatureBreakdown: React.FC<UpgradeFeatureBreakdownProps> = () => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const { environment, vscode } = environmentObj;
    const { user } = userObj;
    const popupRef = React.useRef<HTMLDivElement>(null);

    const closePopup = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleUpgradeClick = async (event: React.MouseEvent) => {
        submitFeedback(user, `User clicked upgrade from feature breakdown popup in ${environment}`);

        closePopup();
        openSubscribe(event, environment, vscode);
    };

    // Handle clicking outside the popup to close it
    React.useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                closePopup();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
                ref={popupRef}
                className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-labelledby="upgrade-modal-title"
            >
                <div className="p-6 relative">
                    {/* Header */}
                    <div className="flex justify-center items-center mb-6">
                        <h2 id="upgrade-modal-title" className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                            Upgrade to Pro
                        </h2>
                    </div>
                    {/* Close Button */}
                    <button
                        onClick={closePopup}
                        className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-xl font-bold"
                        aria-label="Close upgrade modal"
                    >
                        ×
                    </button>

                    {/* Pro Plan Features */}
                    <div className="max-w-md mx-auto mb-8">
                        <div className="border-2 border-cyan-500 dark:border-cyan-400 rounded-lg p-6 relative">
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                <span className="bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                    UPGRADE NOW
                                </span>
                            </div>
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-semibold text-cyan-600 dark:text-cyan-400 mb-2">
                                    Pro Plan
                                </h3>
                                <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                                    $5
                                </div>
                                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                    per month
                                </div>
                            </div>
                            <ul className="space-y-3">
                                {PRO_FEATURES.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <span className="text-cyan-500 mr-3">✓</span>
                                        <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-center">
                        <button
                            onClick={handleUpgradeClick}
                            className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center"
                            aria-label="Upgrade to Pro plan and go to pricing page"
                        >
                            ✨ Upgrade to Pro ✨
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpgradeFeatureBreakdown;